/**
 * NewChat — chat-first BRD-entry experience, ported to the SAME mockup
 * chrome as DesignView (mockups/full-flow.html state 5).
 *
 * Layout (class-for-class with app-shell.css):
 *   .app-shell (grid)
 *     ├─ .appbar      → wordmark + Help + avatar (no breadcrumb yet — no design)
 *     ├─ .leftrail    → search pill, "+ New project" no-op, project tree (from listDesigns), footer
 *     ├─ .canvas-area → welcome canvas-doc ("What are we designing today?") + quick-action pills
 *     └─ .rightrail   → message scrollback (shared renderMessage) + composer-pill
 *
 * Wiring preserved from the old MUI version:
 *   - BRD upload (PDF/DOCX) + data-mapping upload (XLSX/CSV) via the
 *     composer-pill + button menu
 *   - On send-with-BRD: uploadDocument → createDesign → navigate to /design/:id
 *   - useStreamingChat is NOT used here — there's no design id yet, so
 *     all bubbles are local state until createDesign returns.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import RailNav from '../components/RailNav';
import ThemeToggle from '../components/ThemeToggle';
import HelpDialog from '../components/HelpDialog';
import HoursSavedDialog from '../components/HoursSavedDialog';
import FeedbackDialog from '../components/FeedbackDialog';
import {
  uploadDocument,
  uploadKnowledgeBaseFile,
  createDesign,
  listDesigns,
  getDesign,
} from '../services/api';
import {
  renderMessage as renderChatMessage,
  formatBytes,
  fileExt,
  RichMarkdown,
} from '../components/chat/renderMessage';
import ProjectTree from '../components/ProjectTree';
import NewProjectModal from '../components/NewProjectModal';
import NewWorkflowModal from '../components/NewWorkflowModal';
import CustomerPromptDialog from '../components/CustomerPromptDialog';
import FilePreviewOverlay from '../components/FilePreviewOverlay';
import { alertDialog, confirm as confirmDialog } from '../components/ConfirmDialog';
import { cleanupOrphanedCache } from '../utils/cleanupCache';

// ── helpers ────────────────────────────────────────────────────────────

// Capabilities welcome that persists as the first message in the
// right-rail chat transcript. Mirrors AEP Chat Agent's _capabilities_text
// and DesignView's persistent welcome. Always rendered; remains visible
// as the first assistant message even after the user posts turns.
const WELCOME_BODY = `Hi 👋

I'm your **AJO Design Agent** for Adobe Journey Optimizer. I'll guide you through a complete AJO design, one stage at a time.

I can help you with:

**Design stages**
- 👥 **Audience segments** — draft segment definitions with valid PQL
- 📣 **Channel strategy** — per-channel specs for email, SMS, push, in-app, and direct mail
- 🧠 **Decisioning logic** — offer selection and ranking rules
- 🗺️ **Journey flows** — map the end-to-end journey from entry to exit
- ✅ **QA review** — validate the design against best practices
- 📄 **Export** — generate the design document (HTML / DOCX)

**Getting started**
- 📎 **Upload a BRD** — PDF or DOCX; I'll extract the requirements
- 📊 **Add a data mapping** — XLSX/CSV for AEP attribute references
- 🖼️ **Add supporting files** — screenshots, mockups, or any docs to guide the designs in this project
- 💬 **Or just describe the campaign** — and I'll take it from there

What would you like to design today?`;

function getInitials(user) {
  if (!user) return 'JA';
  const name = user.name || user.email || '';
  if (!name) return 'JA';
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  if (parts.length === 0) return 'JA';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

// `getProjectName` and `getDesignTitle` were lifted into the shared
// ProjectTree component. NewChat no longer renders the tree directly so it
// doesn't need them; DesignView still imports them from ProjectTree for
// breadcrumb / page-title rendering.

// ─── Component ─────────────────────────────────────────────────────────

export default function NewChat() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // ── chat + upload state ──────────────────────────────────────────────
  const [messages, setMessages] = useState([]);
  const [brdFile, setBrdFile] = useState(null);
  const [dataMappingFile, setDataMappingFile] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [error, setError] = useState(null);
  const [composerInput, setComposerInput] = useState('');
  // Customer for auto-created projects. Empty until the CustomerPromptDialog
  // captures it on the first send; later sends this session reuse it.
  const [composerCustomer, setComposerCustomer] = useState('');
  const [customerPromptOpen, setCustomerPromptOpen] = useState(false);
  const [pendingSendText, setPendingSendText] = useState('');
  const [attachOpen, setAttachOpen] = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [hoursSavedOpen, setHoursSavedOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [allDesigns, setAllDesigns] = useState([]);
  // Type override menu states for each staged chip (BRD / Data mapping / Context files)
  const [brdTypeMenuOpen, setBrdTypeMenuOpen] = useState(false);
  const [dataTypeMenuOpen, setDataTypeMenuOpen] = useState(false);
  const [otherTypeMenuOpen, setOtherTypeMenuOpen] = useState({});  // { [idx]: boolean }
  const [designsLoading, setDesignsLoading] = useState(true);
  const [treeQuery, setTreeQuery] = useState('');
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  // ProjectTree's "+" on a project header signals here; we open the
  // NewWorkflowModal scoped to that project. The shellDesignId is the
  // join key the new row will carry as `projectId` so it groups with
  // its siblings in the tree.
  const [newWorkflow, setNewWorkflow] = useState(null); // {projectId, projectName, shellDesignId} | null
  const [creatingWorkflow, setCreatingWorkflow] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [leftrailCollapsed, setLeftrailCollapsed] = useState(() => {
    try { return localStorage.getItem('design-leftrail-collapsed') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('design-leftrail-collapsed', leftrailCollapsed ? '1' : '0'); } catch {}
  }, [leftrailCollapsed]);

  // DesignView's "+ New project" button navigates here with
  // `state.openNewProject = true` to auto-open the modal. We strip the
  // flag immediately so a manual refresh of "/" doesn't re-trigger it.
  useEffect(() => {
    if (location.state?.openNewProject) {
      setNewProjectOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refs
  const appShellRef = useRef(null);
  const messagesEndRef = useRef(null);
  const composerInputRef = useRef(null);
  const brdInputRef = useRef(null);
  const dataMappingInputRef = useRef(null);
  const otherInputRef = useRef(null);
  // Unified picker for the composer + button — no type menu, auto-classify
  const anyFileInputRef = useRef(null);
  // `otherFiles` is an array so the user can attach any number of
  // supporting docs (screenshots, slide decks, additional spreadsheets,
  // …) in addition to the BRD + data-mapping. Each entry is a File
  // object; the upload pass uploads them sequentially to
  // `{cid}/{wid}/inputs/<filename>` and the agent receives them via
  // the same attachment-note channel as the BRD.
  const [otherFiles, setOtherFiles] = useState([]);
  // Keep the old single-`otherFile` name as a computed alias so the
  // first chip + the rest of the file flow keep working with minimal
  // churn elsewhere in this file.
  const otherFile = otherFiles[0] || null;
  const setOtherFile = (next) => {
    setOtherFiles((prev) => {
      if (next == null) return [];
      if (typeof next === 'function') {
        const updated = next(prev[0] || null);
        return updated ? [updated, ...prev.slice(1)] : prev.slice(1);
      }
      return [next, ...prev.slice(1)];
    });
  };

  // No auto-ack on attach — the staged-file chip in the composer already
  // shows what's attached, and posting an assistant bubble before the user
  // has sent anything reads like the agent replied unprompted.

  // Best-effort load of all designs for the leftrail tree.
  // SWR cache: read localStorage snapshot first (TTL 5min), then fetch
  // in background and overwrite both state and cache.
  const cacheSwept = useRef(false);
  useEffect(() => {
    const consultantId = user?.email;
    if (!consultantId) return;
    let cancelled = false;
    const cacheKey = `ajo-designs-snapshot:${consultantId}`;

    // Stale-while-revalidate: paint ANY cached snapshot instantly (even an
    // old one beats a skeleton), then let the background fetch below replace
    // it. The cache is also invalidated on every mutation broadcast, so
    // staleness only ever spans changes made from another device/session.
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) {
        const snapshot = JSON.parse(raw);
        if (Array.isArray(snapshot.designs) && snapshot.designs.length) {
          setAllDesigns(snapshot.designs);
          setDesignsLoading(false);
        }
      }
    } catch {}

    // Fetch in background regardless of cache hit
    listDesigns(consultantId)
      .then((data) => {
        if (!cancelled) {
          const items = data?.designs || [];
          setAllDesigns(items);
          setDesignsLoading(false);
          // Write to cache
          try {
            localStorage.setItem(cacheKey, JSON.stringify({ timestamp: Date.now(), designs: items }));
          } catch {}
          // Cleanup orphaned cache once per mount
          if (!cacheSwept.current) {
            cacheSwept.current = true;
            const designIds = items.map((d) => d.designId || d.workflowId || d.id).filter(Boolean);
            const projectIds = [...new Set(items.map((d) => d.projectId).filter(Boolean))];
            cleanupOrphanedCache(designIds, projectIds);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.email]);

  // Listen for cache-invalidation broadcasts so leftrail refreshes.
  // Wipe the cache AND re-fetch the designs list to populate the tree
  // with the mutation's result (new design, renamed design, deleted design).
  useEffect(() => {
    function onInvalidate() {
      const consultantId = user?.email;
      if (!consultantId) return;
      try {
        localStorage.removeItem(`ajo-designs-snapshot:${consultantId}`);
      } catch {}
      // Re-fetch immediately to pick up the mutation that triggered the invalidation
      listDesigns(consultantId)
        .then((data) => {
          const items = data?.designs || [];
          setAllDesigns(items);
        })
        .catch(() => {});
    }
    window.addEventListener('ajo:design-cache-invalidated', onInvalidate);
    return () => window.removeEventListener('ajo:design-cache-invalidated', onInvalidate);
  }, [user?.email]);

  // Auto-scroll messages.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // ── Generate / send pipeline ────────────────────────────────────────
  // Supports three entry points from the dashboard:
  //   1. BRD attached (+ optional data-mapping, supporting files) →
  //      classic design-creation flow.
  //   2. Text + supporting files only (no BRD) → empty design row,
  //      files land in `inputs/`, chat opens with the user's prompt
  //      as the first turn.
  //   3. Text only (no files) → empty design row, no S3 uploads, chat
  //      opens with the user's prompt as the first turn.
  const startGeneration = async (initialGuidance = '', customerForProject = '') => {
    if (generating || uploadingFiles) return;
    const trimmedGuidance = (initialGuidance || '').trim();
    if (!brdFile && !dataMappingFile && otherFiles.length === 0 && !trimmedGuidance) {
      setError('Type a message or attach at least one file to start.');
      return;
    }
    // Sending auto-creates a project; handleSend prompts for the customer
    // before we get here, so one of these is always set.
    const projectCustomer = (customerForProject || composerCustomer).trim();
    setError(null);
    setUploadingFiles(true);
    setGenerating(true);

    const fileNames = [
      brdFile && brdFile.name,
      dataMappingFile && dataMappingFile.name,
      ...otherFiles.map((f) => f.name),
    ].filter(Boolean);
    const fileSummary = fileNames.length
      ? `Uploading ${fileNames.map((n) => `**${n}**`).join(', ')} and `
      : '';
    setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content:
          `${fileSummary}opening the design view. ` +
          (trimmedGuidance || fileNames.length
            ? "I'll hand this to the chat agent there and it'll get started."
            : 'Once the design is open, tell me what you want to design.'),
      },
    ]);

    try {
      const consultantEmail = user?.email;
      if (!consultantEmail) {
        throw new Error('Sign-in required — your Okta session has no email claim. Try signing out and back in.');
      }
      const workflowId = `wf_${Math.random().toString(36).slice(2, 14)}`;
      const consultantId = consultantEmail;

      // Upload whatever files the user attached. Each call lands in
      // `{cid}/{wid}/inputs/<filename>` so handle_get_design's leftrail
      // listing picks them all up.
      // Collect every uploaded file's s3Key so we can stitch the
      // [Attached to this workflow] note the agent reads — same shape the
      // in-design composer (submitComposer) produces. Without this the
      // creation flow uploaded files but never told the agent or showed
      // the user a persistent record of what landed.
      const uploaded = [];
      // Parallelize all uploads — BRD, data mapping, and supporting files.
      const uploadPromises = [];
      if (brdFile) {
        uploadPromises.push(
          uploadDocument(brdFile, consultantId, consultantEmail, workflowId)
            .then((result) => {
              if (result?.s3Key) {
                uploaded.push({ label: 'BRD', name: brdFile.name, s3Key: result.s3Key, contentType: brdFile.type || '' });
              }
              return result;
            })
        );
      }
      if (dataMappingFile) {
        uploadPromises.push(
          uploadDocument(dataMappingFile, consultantId, consultantEmail, workflowId)
            .then((result) => {
              if (result?.s3Key) {
                uploaded.push({ label: 'data mapping', name: dataMappingFile.name, s3Key: result.s3Key, contentType: dataMappingFile.type || '' });
              }
              return result;
            })
        );
      }
      for (const f of otherFiles) {
        uploadPromises.push(
          uploadDocument(f, consultantId, consultantEmail, workflowId)
            .then((result) => {
              if (result?.s3Key) {
                uploaded.push({ label: 'supporting file', name: f.name, s3Key: result.s3Key, contentType: f.type || '' });
              }
              return result;
            })
            .catch((uploadErr) => {
              // eslint-disable-next-line no-console
              console.warn(`[NewChat] upload of ${f.name} failed:`, uploadErr);
              return null;
            })
        );
      }
      const allResults = await Promise.all(uploadPromises);
      const brdResult = brdFile ? allResults[0] : null;
      const dmResult = dataMappingFile ? allResults[brdFile ? 1 : 0] : null;
      const result = await createDesign(
        brdResult?.documentId || null,
        brdResult?.s3Key || null,
        dmResult?.s3Key || null,
        consultantId,
        consultantEmail,
        trimmedGuidance,
        '',
        { workflowId, customerName: projectCustomer },
      );
      // Optimistically add the newly-created design to allDesigns so the
      // leftrail search/tree reflects it immediately without waiting for
      // the ConsultantIndex GSI to replicate (can take 2+ seconds). The
      // background listDesigns fetch (triggered by cache invalidation on
      // navigation) will overwrite with the real row once it lands.
      if (result?.designId) {
        setAllDesigns((prev) => [
          ...prev,
          {
            designId: result.designId,
            workflowId: result.workflowId || workflowId,
            consultantId,
            consultantEmail,
            version: result.version || 1,
            designName: result.designName || trimmedGuidance || 'Design Document',
            projectId: result.projectId || result.designId,
            customerName: projectCustomer,
            brdDocumentS3Key: brdResult?.s3Key || null,
            createdAt: Math.floor(Date.now() / 1000),
          },
        ]);
      }
      // Build the first user turn so DesignView's initialGuidance kickoff
      // renders persistent file chips AND invokes the agent — mirroring
      // submitComposer. The attachmentNote (with s3Keys) is what lets the
      // agent's "files in inputs/ are available — reference by s3Key"
      // rule pick the upload up; the placeholder gives a non-empty turn
      // when the user uploaded files without typing anything, so the
      // agent actually starts (extract_brd) instead of sitting idle.
      const attachmentNote = uploaded.length
        ? '\n\n[Attached to this workflow]\n' +
          uploaded.map((u) => `- ${u.label}: ${u.name} (s3Key: ${u.s3Key})`).join('\n')
        : '';
      const fullText =
        (trimmedGuidance || (attachmentNote ? 'I attached file(s) — see below.' : '')) +
        attachmentNote;
      // Images (supporting screenshots/mockups) go to the agent as Bedrock
      // vision content blocks for the first turn, same as submitComposer.
      const imageAttachments = uploaded
        .filter((u) => u.s3Key && (
          (u.contentType || '').startsWith('image/')
          || /\.(png|jpe?g|gif|webp)$/i.test(u.name || '')
        ))
        .map((u) => ({ s3Key: u.s3Key, contentType: u.contentType || '', name: u.name }));

      navigate(`/design/${result.designId}`, {
        state: {
          initialGuidance: fullText || undefined,
          initialAttachments: imageAttachments.length ? imageAttachments : undefined,
        },
      });
    } catch (err) {
      console.error('Generate error:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to start design generation';
      setError(msg);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Sorry, something went wrong: ${msg}` },
      ]);
      setUploadingFiles(false);
      setGenerating(false);
    }
  };

  const handleSend = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed && !brdFile && !dataMappingFile && otherFiles.length === 0) return;
    // Sending auto-creates a project. Ask which customer it's for the
    // first time (per session); later sends reuse the answer.
    if (!composerCustomer.trim()) {
      setPendingSendText(trimmed);
      setCustomerPromptOpen(true);
      return;
    }
    if (trimmed) {
      setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    }
    startGeneration(trimmed, composerCustomer);
  };

  // Quick-action chip prefills the composer instead of immediately
  // sending — gives the user a chance to tweak before hitting Enter.
  const prefillComposer = (text) => {
    setComposerInput(text);
    composerInputRef.current?.focus();
  };

  // ── Composer ────────────────────────────────────────────────────────
  const composerBusy = generating || uploadingFiles;

  const submitComposer = () => {
    const text = composerInput.trim();
    if (!text && !brdFile && !dataMappingFile) return;
    if (composerBusy) return;
    handleSend(text);
    setComposerInput('');
  };

  const handleComposerKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submitComposer();
    }
  };

  const autosize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    const max = parseInt(getComputedStyle(el).maxHeight, 10) || 200;
    el.style.height = Math.min(el.scrollHeight, max) + 'px';
  };
  useEffect(() => { autosize(composerInputRef.current); }, [composerInput]);

  // ── File input handlers ─────────────────────────────────────────────
  // Single auto-classify router for the unified picker. Detects file type
  // from extension and stages into the right bucket. All files accepted;
  // unknown extensions go to context (otherFiles).
  const routeAttachedFiles = (files) => {
    if (!Array.isArray(files) || files.length === 0) return;
    for (const f of files) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (['pdf', 'docx', 'doc'].includes(ext)) {
        setBrdFile(f);
      } else if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setDataMappingFile(f);
      } else {
        // Everything else → context/supporting files
        setOtherFiles((prev) => {
          const byName = new Map(prev.map((p) => [p.name, p]));
          byName.set(f.name, f);
          return Array.from(byName.values());
        });
      }
    }
    // Return focus to the composer after attaching so Enter sends right
    // away — the file picker/drop steals focus otherwise.
    composerInputRef.current?.focus?.();
  };

  // Legacy handlers for back-compat with hidden typed inputs (if any
  // stale refs remain). New flows use routeAttachedFiles directly.
  const handleBrdFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setBrdFile(file);
    if (e.target) e.target.value = '';
    setAttachOpen(false);
  };
  const handleDataMappingFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setDataMappingFile(file);
    if (e.target) e.target.value = '';
    setAttachOpen(false);
  };
  const handleOtherFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setOtherFiles((prev) => {
        const byName = new Map(prev.map((f) => [f.name, f]));
        for (const f of files) byName.set(f.name, f);
        return Array.from(byName.values());
      });
    }
    if (e.target) e.target.value = '';
    setAttachOpen(false);
  };

  // attachOpen state removed — the + button now opens a single picker
  // directly (no sub-menu). Cleanup listener removed with it.

  // Close avatar + help dropdowns on outside click or Escape.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.('[data-menu]')) {
        setAvatarMenuOpen(false);
        setHelpMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setAvatarMenuOpen(false);
        setHelpMenuOpen(false);
      }
    };
    document.addEventListener('click', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // Close type override menus on outside click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest('.msg-file-chip-staged-type')) {
        setBrdTypeMenuOpen(false);
        setDataTypeMenuOpen(false);
        setOtherTypeMenuOpen({});
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  // ── Right-rail resize ───────────────────────────────────────────────
  // When the leftrail is collapsed, `.no-leftrail` declares
  // `grid-template-columns: 1fr 380px !important`, so an inline 3-column
  // style without `!important` loses the cascade fight and the divider
  // appears frozen. Detect `.no-leftrail` and emit 2 columns + use
  // setProperty(..., 'important') to win.
  useEffect(() => {
    let resizing = null;
    const onMouseDown = (e) => {
      const handle = e.target.closest?.('.rightrail-resize');
      if (!handle) return;
      e.preventDefault();
      const shell = appShellRef.current;
      if (!shell) return;
      const cols = getComputedStyle(shell).gridTemplateColumns.split(' ');
      const initialW = parseFloat(cols[cols.length - 1]) || 380;
      resizing = { handle, shell, startX: e.clientX, startW: initialW };
      handle.classList.add('dragging');
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    };
    const onMouseMove = (e) => {
      if (!resizing) return;
      const dx = resizing.startX - e.clientX;
      const w = Math.max(380, Math.min(720, resizing.startW + dx));
      const leftrailHidden = resizing.shell.classList.contains('no-leftrail');
      const tpl = leftrailHidden ? `1fr ${w}px` : `280px 1fr ${w}px`;
      resizing.shell.style.setProperty('grid-template-columns', tpl, 'important');
    };
    const onMouseUp = () => {
      if (!resizing) return;
      resizing.handle.classList.remove('dragging');
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      resizing = null;
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // NewChat is always chat-only (`.no-canvas`): the layout is just
  // leftrail + chat, driven entirely by the `.no-canvas` / `.no-leftrail`
  // CSS classes (2-col / 1-col). There is NO canvas column to size, so we
  // must NOT write an inline `grid-template-columns` here — the old
  // 3-column template (`280px 1fr <chatW>px`) allocated a phantom middle
  // column for the hidden canvas and squished the chat into a narrow third
  // column. Just clear any stale inline override so the class templates win.
  useEffect(() => {
    appShellRef.current?.style.removeProperty('grid-template-columns');
  }, [leftrailCollapsed]);

  // ── Derived ─────────────────────────────────────────────────────────
  const initials = getInitials(user);
  const designCount = allDesigns.length;

  // Shared renderer — no design-loaded handlers wired since the home
  // page never receives checkpoint / agent_question / inline-edit kinds.
  const renderMessage = (m, idx) => renderChatMessage(m, idx, {});

  return (
    <div
      // Always chat-only here: the new-project / landing view has no design
      // output yet, and the canvas is the OUTPUT surface — so we show only
      // the chat. The canvas appears later in DesignView, once a design is
      // generated. `no-canvas` collapses the grid to leftrail + chat.
      className={`app-shell no-canvas${leftrailCollapsed ? ' no-leftrail' : ''}`}
      ref={appShellRef}
    >
      {leftrailCollapsed && (
        <button
          type="button"
          className="leftrail-show-tab"
          onClick={() => setLeftrailCollapsed(false)}
          title="Show projects"
          aria-label="Show projects sidebar"
        >
          <span className="leftrail-show-grip" aria-hidden="true" />
        </button>
      )}
      {/* Hidden file inputs — legacy typed refs kept for back-compat */}
      <input ref={brdInputRef} type="file" hidden accept=".pdf,.docx,.doc" onChange={handleBrdFileChange} />
      <input ref={dataMappingInputRef} type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleDataMappingFileChange} />
      <input ref={otherInputRef} type="file" multiple hidden onChange={handleOtherFileChange} />
      {/* Unified picker for the composer + button — accepts any file, auto-classifies */}
      <input
        ref={anyFileInputRef}
        type="file"
        hidden
        multiple
        onChange={(e) => {
          routeAttachedFiles(Array.from(e.target.files || []));
          if (e.target) e.target.value = '';
        }}
      />

      {/* ── App bar ─────────────────────────────────────────────── */}
      <header className="appbar">
        <div className="appbar-left">
          <button
            type="button"
            className="appbar-sidebar-toggle"
            onClick={() => setLeftrailCollapsed((v) => !v)}
            title={leftrailCollapsed ? 'Show projects' : 'Hide projects'}
            aria-label={leftrailCollapsed ? 'Show projects sidebar' : 'Hide projects sidebar'}
            aria-pressed={!leftrailCollapsed}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M2 3h12M2 8h12M2 13h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          <div className="wordmark">
            <span className="wordmark-logo">AJO</span> Design Agent
          </div>
        </div>
        <div className="top-actions">
          <ThemeToggle />
          <span className="menu-anchor" data-menu>
            <button
              type="button"
              title="Help & feedback"
              onClick={(e) => {
                e.stopPropagation();
                setHelpMenuOpen((v) => !v);
                setAvatarMenuOpen(false);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 8,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 6,
                color: 'var(--text-secondary)',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                <circle cx="9" cy="9" r="7.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M9 13v-1M9 9.5c0-.5.3-.9.7-1.1.8-.4 1.3-1.2 1.3-2.1 0-1.3-1-2.3-2.2-2.3C7.6 4 6.6 5 6.6 6.3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            {helpMenuOpen && (
              <div className="topmenu" data-menu>
                <button
                  className="topmenu-item"
                  onClick={() => {
                    setHelpMenuOpen(false);
                    setFeedbackOpen(true);
                  }}
                >
                  <span className="glyph">📝</span>
                  <span className="label">Send feedback</span>
                </button>
                <button
                  className="topmenu-item"
                  onClick={() => {
                    setHelpMenuOpen(false);
                    setHoursSavedOpen(true);
                  }}
                >
                  <span className="glyph">⏱</span>
                  <span className="label">Log hours saved</span>
                </button>
                <div className="topmenu-divider" />
                <button
                  className="topmenu-item"
                  onClick={() => {
                    setHelpMenuOpen(false);
                    setHelpDialogOpen(true);
                  }}
                >
                  <span className="glyph">⌨</span>
                  <span className="label">Keyboard shortcuts</span>
                </button>
              </div>
            )}
          </span>
          <span className="top-divider" />
          <span className="menu-anchor" data-menu>
            <button
              className="avatar avatar-button"
              title={user?.email || ''}
              onClick={(e) => {
                e.stopPropagation();
                setAvatarMenuOpen((v) => !v);
                setHelpMenuOpen(false);
              }}
            >
              {initials}
            </button>
            {avatarMenuOpen && (
              <div className="topmenu" data-menu>
                {user?.email && (
                  <>
                    <div className="topmenu-item" style={{ cursor: 'default' }}>
                      <span className="label" style={{ color: 'var(--text-tertiary)', fontSize: 11.5 }}>
                        Signed in as<br />
                        <span style={{ color: 'var(--text-primary)', fontSize: 12.5 }}>{user.email}</span>
                      </span>
                    </div>
                    <div className="topmenu-divider" />
                  </>
                )}
                <button
                  className="topmenu-item"
                  onClick={() => {
                    setAvatarMenuOpen(false);
                    logout();
                  }}
                >
                  <span className="glyph">⎋</span>
                  <span className="label">Sign out</span>
                </button>
              </div>
            )}
          </span>
        </div>
      </header>

      {/* ── Left rail ───────────────────────────────────────────── */}
      <aside className="leftrail">
        <RailNav
          active="home"
          onNewProject={() => setNewProjectOpen(true)}
          search={(
            <div className="leftrail-search">
              <span>⌕</span>
              <input
                type="text"
                placeholder="Search projects & chats…"
                value={treeQuery}
                onChange={(e) => setTreeQuery(e.target.value)}
              />
              <span className="kbd-hint">⌘K</span>
            </div>
          )}
        />
        <div className="tree">
          <ProjectTree
            designs={allDesigns}
            query={treeQuery}
            onAfterDelete={(did) =>
              setAllDesigns((prev) => (prev || []).filter((x) => {
                const xid = x.designId || x.workflowId || x.id;
                if (xid === did) return false;
                // Also filter out child workflows if the deleted id was
                // their project shell (backend cascades the delete
                // server-side but the leftrail needs optimistic removal
                // of children too).
                if (x.projectId === did && xid !== did) return false;
                return true;
              }))
            }
            onAfterUpdate={(did, patch) =>
              setAllDesigns((prev) => (prev || []).map(
                (x) => ((x.designId || x.workflowId || x.id) === did ? { ...x, ...patch } : x),
              ))
            }
            consultantId={user?.email}
            consultantEmail={user?.email}
            onNewWorkflow={(ctx) => setNewWorkflow(ctx)}
            onOpenInputFile={(f) => setPreviewFile(f)}
            isLoading={designsLoading}
            onUploadInputFile={async (targetDid, file) => {
              const consultantEmail = user?.email;
              if (!consultantEmail || !file) return;
              const consultantId = consultantEmail;
              const target = (allDesigns || []).find(
                (d) => (d.designId || d.workflowId || d.id) === targetDid,
              );
              let wid = target?.workflowId;
              try {
                const fresh = await getDesign(targetDid);
                wid = fresh?.workflowId
                  || (fresh?.brdDocumentS3Key || '').split('/')[1]
                  || wid;
              } catch {
                /* fall through */
              }
              if (!wid) {
                await alertDialog({
                  title: 'Upload failed',
                  message: 'Could not resolve the workflow id for this row. Refresh and try again.',
                });
                return;
              }
              try {
                await uploadDocument(file, consultantId, consultantEmail, wid);
              } catch (err) {
                await alertDialog({
                  title: 'Upload failed',
                  message: err?.response?.data?.error || err?.message || 'unknown error',
                });
              }
            }}
          />
        </div>
        <div className="leftrail-footer">
          <span>{designCount} design{designCount === 1 ? '' : 's'}</span>
        </div>
      </aside>

      {/* Canvas is intentionally omitted here (chat-only): no design output
          exists on the landing / new-project view. File preview is a
          full-surface overlay that used to live inside .canvas-area; render
          it at the shell level so it still works without the canvas column. */}
      <FilePreviewOverlay
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />

      {/* ── Right rail (chat) ───────────────────────────────────── */}
      <aside className="rightrail">
        <div className="rightrail-resize" title="Drag to resize" />
        {error && (
          <div className="draft-banner" style={{ background: '#FFEEEC', borderColor: '#FCA5A5', margin: '12px 16px 0' }}>
            <div className="draft-banner-icon" style={{ background: '#dc2626' }}>!</div>
            <div className="draft-banner-text">{error}</div>
            <div className="draft-banner-actions">
              <button className="btn btn-outline" onClick={() => setError(null)}>Dismiss</button>
            </div>
          </div>
        )}
        <div className="messages">
          {/* Persistent welcome — always rendered as the first message in the
              transcript, matching AEP Chat Agent behavior. */}
          <div className="chat-welcome">
            <div className="chat-welcome-avatar" aria-hidden="true">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 0l1.9 6.1L16 8l-6.1 1.9L8 16 6.1 9.9 0 8l6.1-1.9L8 0z" />
              </svg>
            </div>
            <div className="chat-welcome-body">
              <RichMarkdown content={WELCOME_BODY} />
            </div>
          </div>
          {messages.map(renderMessage)}
          <div ref={messagesEndRef} />
        </div>

        <div className="composer-pill">
          <textarea
            ref={composerInputRef}
            className="composer-pill-input"
            rows={1}
            placeholder="Drop a BRD or describe the campaign…"
            value={composerInput}
            onChange={(e) => setComposerInput(e.target.value)}
            onKeyDown={handleComposerKeyDown}
            disabled={composerBusy}
          />
          {/* Staged-file chip(s) — BRD + data-mapping + 0..N supporting files.
              Each chip shows a type caption (BRD / Data mapping / Context) that
              opens a quiet menu on click for reclassification. Compact pill design. */}
          {(brdFile || dataMappingFile || otherFiles.length > 0) && (
            <div style={{ display: 'flex', gap: 8, padding: '0 4px', flexWrap: 'wrap' }}>
              {brdFile && (
                <div className="msg-file-chip-staged">
                  <div className="msg-file-chip-staged-icon">{fileExt(brdFile.name)}</div>
                  <div className="msg-file-chip-staged-body">
                    <div className="msg-file-chip-staged-name" title={brdFile.name}>{brdFile.name}</div>
                    <div className="msg-file-chip-staged-meta">
                      {formatBytes(brdFile.size)}
                      <span style={{ color: 'var(--border-strong)' }}>·</span>
                      <span
                        className="msg-file-chip-staged-type"
                        onClick={() => setBrdTypeMenuOpen(!brdTypeMenuOpen)}
                        title="Click to reclassify"
                      >
                        BRD
                        <div className={`msg-file-chip-staged-type-menu ${brdTypeMenuOpen ? 'open' : ''}`}>
                          <button onClick={() => { setBrdTypeMenuOpen(false); }}>BRD</button>
                          <button onClick={() => { setDataMappingFile(brdFile); setBrdFile(null); setBrdTypeMenuOpen(false); }}>Data mapping</button>
                          <button onClick={() => { setOtherFiles(prev => [...prev, brdFile]); setBrdFile(null); setBrdTypeMenuOpen(false); }}>Context</button>
                        </div>
                      </span>
                    </div>
                  </div>
                  <button className="msg-file-chip-staged-remove" onClick={() => setBrdFile(null)} title="Remove">×</button>
                </div>
              )}
              {dataMappingFile && (
                <div className="msg-file-chip-staged">
                  <div className="msg-file-chip-staged-icon">{fileExt(dataMappingFile.name)}</div>
                  <div className="msg-file-chip-staged-body">
                    <div className="msg-file-chip-staged-name" title={dataMappingFile.name}>{dataMappingFile.name}</div>
                    <div className="msg-file-chip-staged-meta">
                      {formatBytes(dataMappingFile.size)}
                      <span style={{ color: 'var(--border-strong)' }}>·</span>
                      <span
                        className="msg-file-chip-staged-type"
                        onClick={() => setDataTypeMenuOpen(!dataTypeMenuOpen)}
                        title="Click to reclassify"
                      >
                        Data mapping
                        <div className={`msg-file-chip-staged-type-menu ${dataTypeMenuOpen ? 'open' : ''}`}>
                          <button onClick={() => { setBrdFile(dataMappingFile); setDataMappingFile(null); setDataTypeMenuOpen(false); }}>BRD</button>
                          <button onClick={() => { setDataTypeMenuOpen(false); }}>Data mapping</button>
                          <button onClick={() => { setOtherFiles(prev => [...prev, dataMappingFile]); setDataMappingFile(null); setDataTypeMenuOpen(false); }}>Context</button>
                        </div>
                      </span>
                    </div>
                  </div>
                  <button className="msg-file-chip-staged-remove" onClick={() => setDataMappingFile(null)} title="Remove">×</button>
                </div>
              )}
              {otherFiles.map((f, idx) => (
                <div key={`${f.name}-${idx}`} className="msg-file-chip-staged">
                  <div className="msg-file-chip-staged-icon">{fileExt(f.name)}</div>
                  <div className="msg-file-chip-staged-body">
                    <div className="msg-file-chip-staged-name" title={f.name}>{f.name}</div>
                    <div className="msg-file-chip-staged-meta">
                      {formatBytes(f.size)}
                      <span style={{ color: 'var(--border-strong)' }}>·</span>
                      <span
                        className="msg-file-chip-staged-type"
                        onClick={() => setOtherTypeMenuOpen(prev => ({ ...prev, [idx]: !prev[idx] }))}
                        title="Click to reclassify"
                      >
                        Context
                        <div className={`msg-file-chip-staged-type-menu ${otherTypeMenuOpen[idx] ? 'open' : ''}`}>
                          <button onClick={() => { setBrdFile(f); setOtherFiles(prev => prev.filter((_, i) => i !== idx)); setOtherTypeMenuOpen(prev => ({ ...prev, [idx]: false })); }}>BRD</button>
                          <button onClick={() => { setDataMappingFile(f); setOtherFiles(prev => prev.filter((_, i) => i !== idx)); setOtherTypeMenuOpen(prev => ({ ...prev, [idx]: false })); }}>Data mapping</button>
                          <button onClick={() => { setOtherTypeMenuOpen(prev => ({ ...prev, [idx]: false })); }}>Context</button>
                        </div>
                      </span>
                    </div>
                  </div>
                  <button className="msg-file-chip-staged-remove" onClick={() => setOtherFiles((prev) => prev.filter((_, i) => i !== idx))} title="Remove">×</button>
                </div>
              ))}
            </div>
          )}
          <div className="composer-pill-row">
            <span className="composer-pill-attach">
              <button
                title="Attach file — any type, auto-classified"
                onClick={() => anyFileInputRef.current?.click()}
              >+</button>
            </span>
            <span className="composer-pill-helper">↵ to send</span>
            <button
              className="composer-pill-send"
              title="Send"
              onClick={submitComposer}
              disabled={composerBusy || (!composerInput.trim() && !brdFile && !dataMappingFile && otherFiles.length === 0)}
            >↑</button>
          </div>
        </div>
      </aside>

      <HelpDialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />

      <HoursSavedDialog
        open={hoursSavedOpen}
        designs={[...allDesigns].sort(
          (a, b) =>
            (Date.parse(b.updatedAt || b.lastModified || b.createdAt || '') || 0) -
            (Date.parse(a.updatedAt || a.lastModified || a.createdAt || '') || 0)
        )}
        onClose={() => setHoursSavedOpen(false)}
      />

      <FeedbackDialog
        open={feedbackOpen}
        user={user}
        designs={allDesigns}
        onClose={() => setFeedbackOpen(false)}
      />

      <CustomerPromptDialog
        open={customerPromptOpen}
        designs={allDesigns}
        initialValue={composerCustomer}
        onConfirm={(name) => {
          setCustomerPromptOpen(false);
          setComposerCustomer(name);
          if (pendingSendText) {
            setMessages((prev) => [...prev, { role: 'user', content: pendingSendText }]);
          }
          startGeneration(pendingSendText, name);
          setPendingSendText('');
        }}
        onCancel={() => {
          setCustomerPromptOpen(false);
          // Give the typed message back — nothing was sent.
          if (pendingSendText) setComposerInput(pendingSendText);
          setPendingSendText('');
        }}
      />

      <NewProjectModal
        open={newProjectOpen}
        designs={allDesigns}
        onClose={() => setNewProjectOpen(false)}
        busy={generating || uploadingFiles}
        onCreate={async ({ name, customerName, kbFiles }) => {
          // Project = name + KB only. BRD is added later via a future
          // "+ New design" flow inside the project. KB files go to the
          // consultant's shared `{cid}/kb/` prefix (visible in every
          // project's modal). The project itself persists as a shell
          // design row (status='project_shell') so the sidebar tree
          // has something to render.
          if (generating || uploadingFiles) return;
          setError(null);
          setUploadingFiles(true);
          setGenerating(true);
          // Keep the modal OPEN during creation so its `busy` state shows
          // the "Creating…" label + disables inputs. Closing it here made
          // the modal vanish instantly with zero feedback during the
          // shell+workflow creates (and any KB uploads) — which reads as
          // "nothing happened / not created". On success we navigate away
          // (NewChat unmounts, modal goes with it); on error the catch
          // re-opens it with the message.
          try {
            const consultantEmail = user?.email;
            if (!consultantEmail) {
              throw new Error(
                'Sign-in required — your Okta session has no email claim. Try signing out and back in.',
              );
            }
            const consultantId = consultantEmail;
            for (const f of kbFiles || []) {
              try {
                await uploadKnowledgeBaseFile(f, consultantId, consultantEmail);
              } catch (e) {
                console.warn('KB upload failed:', f.name, e);
              }
            }
            // Phase 1: create the project shell row.
            const shell = await createDesign(
              '',                  // documentId — empty for shells
              '',                  // brdS3Key — empty for shells
              null,                // dataMappingS3Key
              consultantId,
              consultantEmail,
              '',                  // initial guidance n/a for shells
              name,                // designName flows to the sidebar tree
              { projectShell: true, customerName },
            );
            // Phase 2: auto-create an empty workflow row under the
            // shell so the user lands on a real working surface with
            // Inputs / Documents folders ready to use. Without this,
            // the user creates a project, sees an empty shell page,
            // and asks "where do I go from here?" — that ambiguity
            // is what the user reported. The workflow inherits the
            // project name with a "draft" suffix so it's clear which
            // is the project label vs the workflow row.
            let workflow = null;
            try {
              workflow = await createDesign(
                '',                                            // documentId
                '',                                            // brdS3Key
                null,                                          // dataMappingS3Key
                consultantId,
                consultantEmail,
                '',                                            // initial guidance
                `${name} — draft`,                             // designName
                {
                  projectId: shell.designId,
                  workflowId: `wf_${Math.random().toString(36).slice(2, 14)}`,
                  customerName,
                },
              );
            } catch (e) {
              console.warn('auto-workflow creation failed', e);
            }
            // Optimistically add both the shell and workflow to allDesigns
            // so the leftrail search/tree reflects them immediately
            // without waiting for GSI replication.
            const additions = [];
            if (shell?.designId) {
              additions.push({
                designId: shell.designId,
                workflowId: shell.workflowId,
                consultantId,
                consultantEmail,
                version: shell.version || 1,
                designName: name,
                projectId: shell.designId,
                customerName: customerName || '',
                status: 'project_shell',
                createdAt: Math.floor(Date.now() / 1000),
              });
            }
            if (workflow?.designId) {
              additions.push({
                designId: workflow.designId,
                workflowId: workflow.workflowId,
                consultantId,
                consultantEmail,
                version: workflow.version || 1,
                designName: `${name} — draft`,
                projectId: shell.designId,
                customerName: customerName || '',
                createdAt: Math.floor(Date.now() / 1000),
              });
            }
            if (additions.length) {
              setAllDesigns((prev) => [...prev, ...additions]);
            }
            // Land on the workflow when we have one — that's the
            // "real" page with composer + canvas. Fall back to the
            // shell if workflow creation failed (rare DDB hiccup).
            navigate(`/design/${(workflow && workflow.designId) || shell.designId}`);
          } catch (err) {
            console.error('New project error:', err);
            const msg = err.response?.data?.error || err.message || 'Failed to create project';
            setError(msg);
            setUploadingFiles(false);
            setGenerating(false);
            // Reopen so the user can retry without re-typing the name.
            setNewProjectOpen(true);
          }
        }}
      />
      <NewWorkflowModal
        open={!!newWorkflow}
        projectName={newWorkflow?.projectName || ''}
        initialCustomerName={(() => {
          const parentId = newWorkflow?.shellDesignId || newWorkflow?.projectId;
          if (!parentId) return '';
          const rows = allDesigns || [];
          return rows.find((d) => d.designId === parentId)?.customerName
            || rows.find((d) => d.projectId === parentId && d.customerName)?.customerName
            || '';
        })()}
        designs={allDesigns}
        busy={creatingWorkflow}
        onClose={() => !creatingWorkflow && setNewWorkflow(null)}
        onCreate={async ({ name, customerName, brdFile: wfBrd, dataMappingFile: wfDm }) => {
          // Create a new workflow under the selected project. Shape:
          //   1. Generate a per-workflow id (S3 prefix segment)
          //   2. Upload any attached files into {cid}/{wid}/inputs/
          //   3. createDesign({ projectId: shellDesignId, designName, brdS3Key })
          //   4. Navigate to the new design page so the user lands on it
          if (!newWorkflow || creatingWorkflow) return;
          setError(null);
          setCreatingWorkflow(true);
          try {
            const consultantEmail = user?.email;
            if (!consultantEmail) {
              throw new Error('Sign-in required.');
            }
            const consultantId = consultantEmail;
            const wid = `wf_${Math.random().toString(36).slice(2, 14)}`;
            let brdResult = null;
            let dmResult = null;
            if (wfBrd) {
              brdResult = await uploadDocument(wfBrd, consultantId, consultantEmail, wid);
            }
            if (wfDm) {
              dmResult = await uploadDocument(wfDm, consultantId, consultantEmail, wid);
            }
            const result = await createDesign(
              brdResult?.documentId || '',
              brdResult?.s3Key || '',
              dmResult?.s3Key || null,
              consultantId,
              consultantEmail,
              '',
              name,
              { projectId: newWorkflow.shellDesignId || newWorkflow.projectId, customerName },
            );
            const parentId = newWorkflow.shellDesignId || newWorkflow.projectId;
            setAllDesigns((prev) => ([
              ...(prev || []),
              {
                designId: result.designId,
                designName: name,
                projectId: parentId,
                customerName: customerName || '',
                status: 'ready',
                version: 1,
                workflowId: result.workflowId || '',
              },
            ]));
            setNewWorkflow(null);
            navigate(`/design/${result.designId}`);
          } catch (err) {
            console.error('New workflow error:', err);
            const msg = err.response?.data?.error || err.message || 'Failed to create workflow';
            setError(msg);
          } finally {
            setCreatingWorkflow(false);
          }
        }}
      />
    </div>
  );
}
